import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ROLE_LABELS, CHARGE_BY_CPT } from '../mock';
import { fullName, time, dayLabel, money, RISK_LEVELS, TIMEFRAMES, inTimeframe, HDHP_REMAINING } from '../lib/data';
import { useWorklist, useProviders, useLocations, usePlanLearnings, useNetworkPlanProfiles } from '../api/hooks';
import { planKeyFor } from '../lib/networkStore';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import PlanNoteCard from '../components/PlanNoteCard';
import PageHeader from '../components/PageHeader';
import RiskBadge from '../components/RiskBadge';
import Stat from '../components/Stat';
import StatusPill from '../components/StatusPill';
import EmptyState from '../components/EmptyState';
import Briefing from '../components/Briefing';
import { HowItWorksPopover } from '../components/HowItWorks';
import ReasoningTrace from '../components/ReasoningTrace';
import PeekDrawer from '../components/PeekDrawer';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from '../lib/aiToast';
import PractitionerBreakdown from '../components/PractitionerBreakdown';
import FiltersPopover from '../components/FiltersPopover';
import ActiveFilterBreadcrumb from '../components/ActiveFilterBreadcrumb';
import AddAppointment from '../components/AddAppointment';
import CloseLoopDialog from '../components/CloseLoopDialog';
import { Zap, Bot, Check, ChevronRight, ChevronDown, Inbox, CheckCheck, Layers, DollarSign, CalendarPlus, Lightbulb, Users, CalendarClock, ShieldAlert } from 'lucide-react';

const STATE_FILTERS = [
  { k: 'attention', label: 'Needs attention' },
  { k: 'all', label: 'All' },
  { k: 'high', label: 'High risk' },
  { k: 'auto', label: 'Auto-cleared' },
];

// Visit type is a value dimension: new patients & procedures carry more risk and
// revenue than routine follow-ups, so the front desk can slice or group by it.
const VISIT_TYPES = ['New patient', 'Follow-up', 'Procedure', 'Surgery'];
const TYPE_CHIP = { 'New patient': 'pill-indigo', 'Follow-up': 'pill-slate', Procedure: 'pill-violet', Surgery: 'pill-amber' };
const TYPE_ORDER = { 'New patient': 0, Procedure: 1, Surgery: 2, 'Follow-up': 3 };

export default function Worklist() {
  const { session } = useAuth();
  const nav = useNavigate();
  const role = session?.user?.role || 'front_desk';
  const practiceId = session?.practice?.id || 'prac_001';

  // Land each role on what they're accountable for: front desk on the review
  // queue, billing on financial risk, managers/admins on the full picture.
  const canGovern = role === 'manager' || role === 'admin';
  const [timeframe, setTimeframe] = useState('today');
  const [provider, setProvider] = useState('all');
  const [location, setLocation] = useState('all');
  const [payer, setPayer] = useState('all');
  const [filter, setFilter] = useState(() => (role === 'billing' ? 'high' : canGovern ? 'all' : 'attention'));
  const [visitType, setVisitType] = useState('all');
  const [groupByType, setGroupByType] = useState(false);
  const [resolved, setResolved] = useState({});
  const [loopItem, setLoopItem] = useState(null);
  const [autopilot, setAutopilot] = useState(true);
  const [cursor, setCursor] = useState(0);
  const [peek, setPeek] = useState(null);

  const [showProviders, setShowProviders] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const { data: base = [] } = useWorklist(practiceId);
  const { data: providers = [] } = useProviders(practiceId);
  const { data: locations = [] } = useLocations(practiceId);
  const { data: learnings = [] } = usePlanLearnings(practiceId);
  const { data: profiles = [] } = useNetworkPlanProfiles(practiceId);
  // The row "plan note" surfaces the plan's metrics + curated learnings, fronted
  // by the appointment's pre-generated patient narrative. Index both by plan_key.
  const profileByKey = useMemo(() => new Map(profiles.map((p) => [p.planKey, p])), [profiles]);
  const learningsByKey = useMemo(() => {
    const m = new Map();
    learnings.forEach((l) => { if (!m.has(l.planKey)) m.set(l.planKey, []); m.get(l.planKey).push(l); });
    return m;
  }, [learnings]);
  const planKeyOf = (i) => planKeyFor(i.check?.payerId || i.payer?.id, i.check?.planNameReturned || i.plan?.planName);
  // Everything the note needs for a row, or null when there's nothing to show.
  const noteFor = (i) => {
    const key = planKeyOf(i);
    const profile = profileByKey.get(key) || null;
    const planLearnings = learningsByKey.get(key) || [];
    const narrative = i.planNarrative || null;
    if (!profile && !planLearnings.length && !narrative) return null;
    // Amber when there's a real signal (learnings, denials, or elevated plan risk).
    const notable = planLearnings.length > 0 || (profile?.denials?.deniedCount || 0) > 0
      || ['high', 'medium'].includes(profile?.planRisk?.level);
    return { key, profile, learnings: planLearnings, narrative, notable };
  };
  const payers = useMemo(() => Array.from(new Map(base.map((w) => [w.payer?.id, w.payer]).filter(([id]) => id)).values()), [base]);

  // Appointment-level filters (timeframe / location / payer / visit type), then
  // provider on top. Keeping a provider-independent set lets the practitioner
  // breakdown show each provider's load regardless of the active provider filter.
  const scopedNoProvider = base.filter((w) =>
    inTimeframe(w.apt.startTime, timeframe) &&
    (location === 'all' || w.apt.locationId === location) &&
    (payer === 'all' || w.payer?.id === payer) &&
    (visitType === 'all' || w.visitType === visitType));
  const scoped = scopedNoProvider.filter((w) => provider === 'all' || w.apt.providerId === provider);

  // Money the front desk is expected to collect today — copays on active
  // coverage. Reconciling this against the day's deposits is the manager's first
  // signal that a copay was missed at the desk.
  const isActive = (i) => i.check?.coverage?.active !== false;
  const isHdhp = (i) => { const f = i.check?.financials; return isActive(i) && f && ((f.deductibleInd || 0) - (f.deductibleIndMet || 0) > HDHP_REMAINING); };
  // inactive / highDeductible use the exact same predicates as the 'inactive' and
  // 'hdhp' row filters below, so the briefing's "X patients … Show these" count
  // always equals what the filtered queue shows.
  const summary = useMemo(() => ({
    total: scoped.length,
    needsAttention: scoped.filter((i) => i.agentState === 'awaiting_review' || i.agentState === 'flagged').length,
    autoCleared: scoped.filter((i) => i.agentState === 'auto_cleared').length,
    high: scoped.filter((i) => i.risk.level === 'high').length,
    copayToCollect: scoped.reduce((s, i) => s + (isActive(i) ? (i.check?.financials?.copay || 0) : 0), 0),
    inactive: scoped.filter((i) => i.check?.coverage?.active === false).length,
    highDeductible: scoped.filter((i) => isHdhp(i)).length,
    // Revenue at risk for *this* timeframe (keeps the briefing's "$X at stake"
    // scoped to the same set as every other number in the sentence): inactive
    // coverage puts the whole visit at risk; high-risk visits a weighted share.
    atStake: scoped.reduce((s, i) => {
      const charge = CHARGE_BY_CPT[i.apt.cpt] || 150;
      if (i.check?.coverage?.active === false) return s + charge;
      if (i.risk.level === 'high') return s + charge * 0.5;
      return s;
    }, 0),
  }), [scoped]);

  // Human approval resolves a row to 'cleared' — distinct from the agent's
  // 'auto_cleared' so the worklist (and the Auto-cleared filter) never conflates
  // "I signed off on this" with "the agent cleared it without me".
  const stateOf = (i) => (resolved[i.id] ? 'cleared' : i.agentState);
  const items = scoped.filter((i) => {
    const s = stateOf(i);
    if (filter === 'all') return true;
    if (filter === 'attention') return s === 'awaiting_review' || s === 'flagged';
    if (filter === 'high') return i.risk.level === 'high';
    if (filter === 'auto') return s === 'auto_cleared';
    if (filter === 'inactive') return i.check?.coverage?.active === false;
    if (filter === 'hdhp') return isHdhp(i);
    if (filter === 'copay') return isActive(i) && (i.check?.financials?.copay || 0) > 0;
    return true;
  });

  // When grouping, order rows by visit type so each section renders contiguously.
  const view = useMemo(() => (groupByType ? [...items].sort((a, b) => (TYPE_ORDER[a.visitType] ?? 9) - (TYPE_ORDER[b.visitType] ?? 9)) : items), [items, groupByType]);

  const detailHref = (i) => `/app/appointments/${i.id}`;
  // Approve = close the loop: clear the row and post the agent's verified
  // summary back into the PMS (Athena). The dialog is the human-in-the-loop
  // evidence that the write-back happened.
  const approve = (i) => {
    const ts = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    setResolved((r) => ({ ...r, [i.id]: true }));
    setLoopItem({ item: i, ts });
  };
  const pendingInView = view.filter((i) => stateOf(i) === 'awaiting_review');
  const approveAll = () => {
    setResolved((r) => ({ ...r, ...pendingInView.reduce((a, i) => ((a[i.id] = true), a), {}) }));
    toast.success(`Approved ${pendingInView.length} appointments — summaries synced to Athena`, { agent: 'Verification', confidence: 0.95 });
  };
  const clearFilters = () => { setTimeframe('today'); setProvider('all'); setLocation('all'); setPayer('all'); setVisitType('all'); };

  useEffect(() => { setCursor((c) => Math.min(c, Math.max(0, view.length - 1))); }, [filter, timeframe, provider, location, payer, visitType, view.length]);
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.metaKey || e.ctrlKey) return;
      if (!view.length) return;
      if (peek !== null) {
        if (e.key === 'Escape') setPeek(null);
        else if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); setPeek((p) => { const n = Math.min(view.length - 1, p + 1); setCursor(n); return n; }); }
        else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); setPeek((p) => { const n = Math.max(0, p - 1); setCursor(n); return n; }); }
        else if (e.key === 'e') { const it = view[peek]; if (it && stateOf(it) === 'awaiting_review') approve(it); }
        else if (e.key === 'o' || e.key === 'Enter') { const it = view[peek]; if (it) nav(detailHref(it)); }
        return;
      }
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(view.length - 1, c + 1)); }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
      else if (e.key === 'e') { const it = view[cursor]; if (it && stateOf(it) === 'awaiting_review') approve(it); }
      else if (e.key === 'o' || e.key === 'Enter') { const it = view[cursor]; if (it) setPeek(cursor); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, cursor, peek]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show the in-card "By provider" strip toggle only when more than one provider
  // has load in the current (provider-independent) scope — mirrors
  // PractitionerBreakdown's own self-hide so the toggle never opens onto nothing.
  const showProviderToggle = providers.filter((p) => scopedNoProvider.some((i) => i.apt.providerId === p.id)).length > 1;

  // Today/tomorrow are single-day views (date is implied); every other timeframe
  // spans days, so the row needs the date alongside the time.
  const multiDay = timeframe !== 'today' && timeframe !== 'tomorrow';

  const renderRow = (i, idx) => {
    const state = stateOf(i);
    const lvl = RISK_LEVELS[i.risk.level];
    const focused = idx === cursor;
    return (
      <div key={i.id} onClick={() => { setCursor(idx); setPeek(idx); }} className={`row-hover flex items-stretch cursor-pointer group ${focused ? 'bg-brand-50/40' : ''}`}>
        <div className={`w-1 shrink-0 ${focused ? 'bg-brand-500' : lvl.dot}`} />
        <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 items-center px-4 py-2.5">
          {/* time + the issue they're coming in for. When the timeframe spans more
              than one day, lead with the date so the row isn't ambiguous. */}
          <div className="col-span-6 sm:col-span-2 min-w-0">
            {multiDay && <div className="text-[11px] font-medium text-slate-400 tnum">{dayLabel(i.apt.startTime)}</div>}
            <div className="text-sm font-medium text-slate-900 tnum">{time(i.apt.startTime)}</div>
            <div className="text-xs text-slate-500 truncate">{i.apt.reason}{i.location ? ` · ${i.location.name}` : ''}</div>
          </div>
          {/* patient + visit type + plan */}
          <div className="col-span-6 sm:col-span-4 lg:col-span-3 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
              {fullName(i.patient)}
              <span className={`pill ${TYPE_CHIP[i.visitType] || 'pill-slate'}`}>{i.visitType}</span>
            </div>
            <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
              <span className="truncate">{i.plan?.planName || i.check?.planNameReturned || '—'} · {i.payer?.name}</span>
              {(() => {
                const note = noteFor(i);
                if (!note) return null;
                return (
                  <Popover>
                    <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`inline-flex items-center gap-0.5 text-[10px] shrink-0 rounded px-1 -mx-0.5 hover:bg-slate-100 transition-colors ${note.notable ? 'text-amber-600' : 'text-slate-400 hover:text-slate-600'}`}
                        title="Plan metrics + context for this patient"
                      >
                        <Lightbulb className="h-3 w-3" />note
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[26rem]" onClick={(e) => e.stopPropagation()}>
                      <PlanNoteCard
                        profile={note.profile}
                        narrative={note.narrative}
                        learnings={note.learnings}
                        planLabel={i.plan?.planName || i.check?.planNameReturned}
                        payerName={i.payer?.name}
                      />
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </div>
          </div>
          {/* risk + why (one line) */}
          <div className="col-span-12 sm:col-span-3 lg:col-span-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <RiskBadge level={i.risk.level} score={i.risk.score} showScore />
            {i.check && <ReasoningTrace risk={i.risk} />}
          </div>
          {/* next action — the single clear signal (room for it appears at lg) */}
          <div className="hidden lg:block lg:col-span-3 min-w-0">
            {i.actionLabel ? (
              <>
                <div className="text-[13px] font-medium text-slate-800 truncate flex items-center gap-1.5"><Bot className="h-3.5 w-3.5 text-violet-500 shrink-0" />{i.actionLabel}</div>
                <div className="text-xs text-slate-500 truncate">{i.recommended}</div>
              </>
            ) : i.clearedAction?.due ? (
              <div className="text-[13px] font-medium text-amber-700 truncate flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5 shrink-0" />{i.clearedAction.label}</div>
            ) : (
              <div className="text-xs text-slate-400 flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" />{i.clearedAction?.label || 'Cleared — no action needed'}</div>
            )}
          </div>
          {/* action / state */}
          <div className="col-span-12 sm:col-span-3 lg:col-span-2 flex items-center justify-end gap-2">
            {state === 'awaiting_review' ? (
              <Button size="sm" className="h-8 bg-brand-600 hover:bg-brand-700 text-white" onClick={(e) => { e.stopPropagation(); approve(i); }}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
            ) : (
              <><StatusPill status={state} /><ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500" /></>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <PageHeader eyebrow={`${ROLE_LABELS[role]} · Pacific Dermatology Group`} title="Worklist">
        <HowItWorksPopover />
        <Button variant="outline" onClick={() => setAddOpen(true)}><CalendarPlus className="h-4 w-4 mr-1.5" />Add appointment</Button>
      </PageHeader>

      {/* The day's story: the agent-written briefing + proactive findings. */}
      <Briefing practiceId={practiceId} name={session?.user?.name} summary={summary} filter={filter} onFilter={setFilter} />

      {/* At-a-glance KPIs — each tile is a one-click filter into the queue below. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat label="Appointments" value={summary.total} sub={`${summary.autoCleared} auto-cleared`} icon={CalendarClock} tone="indigo" onClick={() => setFilter('all')} />
        <Stat label="Needs attention" value={summary.needsAttention} sub="Review queue" icon={ShieldAlert} tone="amber" onClick={() => setFilter('attention')} />
        <Stat label="High risk" value={summary.high} sub="OON · inactive · auth" icon={ShieldAlert} tone="rose" onClick={() => setFilter('high')} />
        <Stat label="Auto-cleared" value={summary.autoCleared} sub="No action needed" icon={Zap} tone="emerald" onClick={() => setFilter('auto')} />
        <Stat label="To collect" value={money(summary.copayToCollect)} sub="Copays · active coverage" icon={DollarSign} tone="sky" onClick={() => setFilter('copay')} />
      </div>

      {/* One toolbar: view switchers inline, secondary facets + bulk actions right. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="seg">{TIMEFRAMES.map((t) => <button key={t.k} className={timeframe === t.k ? 'active' : ''} onClick={() => setTimeframe(t.k)}>{t.label}</button>)}</div>
          <div className="seg">{STATE_FILTERS.map((f) => <button key={f.k} className={filter === f.k ? 'active' : ''} onClick={() => setFilter(f.k)}>{f.label}</button>)}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FiltersPopover
            provider={provider} setProvider={setProvider}
            location={location} setLocation={setLocation}
            payer={payer} setPayer={setPayer}
            visitType={visitType} setVisitType={setVisitType}
            providers={providers} locations={locations} payers={payers}
            visitTypes={VISIT_TYPES} clearFilters={clearFilters}
          />
          <button onClick={() => setGroupByType((g) => !g)} title="Group by visit type" className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium border transition-colors ${groupByType ? 'bg-brand-50 text-brand-700 border-brand-200' : 'bg-white text-slate-600 border-slate-200 hover:text-slate-900'}`}><Layers className="h-4 w-4" />Group</button>
          {pendingInView.length > 1 && <Button size="sm" variant="outline" className="h-9" onClick={approveAll}><CheckCheck className="h-4 w-4 mr-1.5" />Approve all ({pendingInView.length})</Button>}
          {/* Automation governance is a manager/admin control; others see its state read-only. */}
          {canGovern ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 px-1"><span>{autopilot ? 'Autopilot' : 'Copilot'}</span><Switch checked={autopilot} onCheckedChange={(v) => { setAutopilot(v); toast.message(v ? 'Autopilot on — low-risk auto-clears' : 'Copilot — every check waits for review'); }} /></div>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 px-1"><Zap className="h-3.5 w-3.5 text-emerald-500" />Autopilot on</span>
          )}
        </div>
      </div>

      {/* Keeps hidden facets visible + removable, so collapsing them stays safe. */}
      <ActiveFilterBreadcrumb
        timeframe={timeframe} setTimeframe={setTimeframe}
        provider={provider} setProvider={setProvider}
        location={location} setLocation={setLocation}
        payer={payer} setPayer={setPayer}
        visitType={visitType} setVisitType={setVisitType}
        providers={providers} locations={locations} payers={payers}
        clearFilters={clearFilters}
      />

      <div className="dh-card-flush">
        {showProviderToggle && (
          <button onClick={() => setShowProviders((s) => !s)} className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-slate-500 hover:text-slate-800 border-b border-slate-100 transition-colors">
            <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />By provider</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showProviders ? 'rotate-180' : ''}`} />
          </button>
        )}
        {showProviderToggle && showProviders && (
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/40">
            <PractitionerBreakdown providers={providers} items={scopedNoProvider} selected={provider} onSelect={setProvider} />
          </div>
        )}

        {/* Column header (sm+) — aligned to renderRow's 12-col grid */}
        <div className="dh-col-head hidden sm:flex">
          <div className="w-1 shrink-0" />
          <div className="flex-1 grid grid-cols-12 gap-3 px-4 py-2">
            <div className="col-span-2">Time</div>
            <div className="col-span-4 lg:col-span-3">Patient · plan</div>
            <div className="col-span-3 lg:col-span-2">Risk</div>
            <div className="hidden lg:block lg:col-span-3">Next action</div>
            <div className="col-span-3 lg:col-span-2 text-right">Status</div>
          </div>
        </div>

        <div className="divide-y divide-slate-100">
          {view.length === 0 && <EmptyState icon={Inbox} title="Nothing in this view" hint="No appointments match these filters. Adjust the timeframe or clear filters." action={<Button variant="outline" onClick={() => { clearFilters(); setFilter('all'); }}>Reset</Button>} />}
          {(() => {
            let lastType = null;
            return view.map((i, idx) => {
              const showHeader = groupByType && i.visitType !== lastType;
              lastType = i.visitType;
              const count = showHeader ? view.filter((x) => x.visitType === i.visitType).length : 0;
              return (
                <React.Fragment key={i.id}>
                  {showHeader && (
                    <div className="bg-slate-50/80 px-4 py-2 flex items-center gap-2">
                      <span className={`pill ${TYPE_CHIP[i.visitType] || 'pill-slate'}`}>{i.visitType}</span>
                      <span className="text-xs text-slate-500 tnum">{count} appointment{count === 1 ? '' : 's'}</span>
                    </div>
                  )}
                  {renderRow(i, idx)}
                </React.Fragment>
              );
            });
          })()}
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 text-[11px] text-slate-400">
        <span><span className="kbd">j</span> <span className="kbd">k</span> move</span><span><span className="kbd">o</span> peek</span><span><span className="kbd">e</span> approve</span><span><span className="kbd">⌘K</span> copilot</span>
      </div>

      {peek !== null && view[peek] && (
        <PeekDrawer
          item={view[peek]}
          state={stateOf(view[peek])}
          index={peek}
          total={view.length}
          onPrev={() => setPeek((p) => { const n = Math.max(0, p - 1); setCursor(n); return n; })}
          onNext={() => setPeek((p) => { const n = Math.min(view.length - 1, p + 1); setCursor(n); return n; })}
          onClose={() => setPeek(null)}
          onApprove={() => approve(view[peek])}
          onEscalate={() => { toast.message('Escalated to manager'); setPeek(null); }}
        />
      )}

      <AddAppointment open={addOpen} onClose={() => setAddOpen(false)} practiceId={practiceId} />

      {loopItem && <CloseLoopDialog item={loopItem.item} ts={loopItem.ts} onClose={() => setLoopItem(null)} />}
    </div>
  );
}
